import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useGetQR } from "hooks/useGetQR";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "react-qr-code";

const StyledDialogTitle = styled(DialogTitle)(() => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
}));

const StyledTitleText = styled(Box)(() => ({
  flex: 1,
}));

const StyledDescriptionText = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const StyledQrCenterContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: 260,
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));

const StyledQrCodeContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
}));

type Props = {
  open: boolean;
  onClose: () => void;
};

export const QrCodeLoginModal: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { qr, loading, error, refetch } = useGetQR({ enabled: false });

  useEffect(() => {
    if (open) {
      refetch();
    }
  }, [open, refetch]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <StyledDialogTitle>
        <StyledTitleText>{t("qrModal.loginToQr")}</StyledTitleText>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </StyledDialogTitle>
      <DialogContent>
        <StyledDescriptionText variant="body2">
          {t("qrModal.scanQrCodeToLogin")}
        </StyledDescriptionText>

        <StyledQrCenterContainer>
          {loading || error ? (
            <CircularProgress />
          ) : (
            <StyledQrCodeContainer>
              <QRCode value={qr!} size={220} />
            </StyledQrCodeContainer>
          )}
        </StyledQrCenterContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="error" variant="outlined">
          {t("common.close")}
        </Button>
        <LoadingButton
          loading={loading}
          onClick={() => refetch()}
          variant="contained"
        >
          {t("common.refresh")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
